import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import {
  User, Bell, Download, FileText, Pencil, X, Check,
  Shield, Users, RefreshCw, Trash2, ChevronDown,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Switch } from '../components/ui/switch';
import { useAuth } from '../context/AuthContext';
import { clickstreamAPI } from '../lib/api';
import { reportsAPI, authAPI, adminAPI } from '../lib/api';
import { toast } from 'sonner';

// ─── Helpers ────────────────────────────────────────────────────────────────

const formatTs = (ts) => {
  if (!ts) return '—';
  try { return new Date(ts).toLocaleString('en-IN'); } catch { return ts; }
};

const truncateUA = (ua) => {
  if (!ua) return '—';
  const match = ua.match(/(Chrome|Firefox|Safari|Edge|Opera)[\/\s]([\d.]+)/i);
  if (match) return `${match[1]} ${match[2]}`;
  return ua.slice(0, 40) + (ua.length > 40 ? '…' : '');
};

// ─── Badges ─────────────────────────────────────────────────────────────────

function StatusBadge({ status }) {
  const isActive = status === 'active';
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${isActive ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
      {isActive ? 'Active' : 'Blocked'}
    </span>
  );
}

function RoleBadge({ role }) {
  const colours = { admin: 'bg-primary-100 text-primary-700', editor: 'bg-blue-100 text-blue-700', founder: 'bg-amber-100 text-amber-700' };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium capitalize ${colours[role] || 'bg-slate-100 text-slate-600'}`}>
      {role}
    </span>
  );
}

function SkeletonRow({ cols }) {
  return (
    <tr>
      {Array.from({ length: cols }).map((_, i) => (
        <td key={i} className="px-4 py-3"><div className="h-4 bg-slate-200 rounded animate-pulse" /></td>
      ))}
    </tr>
  );
}

// ─── Actions dropdown — uses portal so it's never clipped ───────────────────

function ActionMenu({ user, currentUserId, onStatusChange, onRoleChange, onDelete }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, right: 0 });
  const btnRef = useRef(null);
  const isSelf = user.id === currentUserId;

  const handleOpen = () => {
    if (btnRef.current) {
      const r = btnRef.current.getBoundingClientRect();
      setPos({ top: r.bottom + 4, right: window.innerWidth - r.right });
    }
    setOpen((o) => !o);
  };

  if (isSelf) return <span className="text-xs text-slate-400">—</span>;

  return (
    <div className="inline-block">
      <button
        ref={btnRef}
        onClick={handleOpen}
        className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-lg border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 transition-colors"
      >
        Actions <ChevronDown className="w-3 h-3" />
      </button>

      {open && createPortal(
        <>
          <div className="fixed inset-0 z-[200]" onClick={() => setOpen(false)} />
          <div
            className="fixed z-[201] w-44 rounded-xl border border-slate-200 bg-white shadow-lg py-1 text-sm"
            style={{ top: pos.top, right: pos.right }}
          >
            {user.status === 'active' ? (
              <button className="w-full text-left px-3 py-1.5 hover:bg-slate-50 text-red-600"
                onClick={() => { setOpen(false); onStatusChange(user.id, 'blocked'); }}>
                Block user
              </button>
            ) : (
              <button className="w-full text-left px-3 py-1.5 hover:bg-slate-50 text-emerald-600"
                onClick={() => { setOpen(false); onStatusChange(user.id, 'active'); }}>
                Unblock user
              </button>
            )}
            <div className="border-t border-slate-100 my-1" />
            <button className="w-full text-left px-3 py-1.5 hover:bg-slate-50 text-slate-700"
              onClick={() => { setOpen(false); onRoleChange(user.id, 'admin'); }}>Make Admin</button>
            <button className="w-full text-left px-3 py-1.5 hover:bg-slate-50 text-slate-700"
              onClick={() => { setOpen(false); onRoleChange(user.id, 'editor'); }}>Make Editor</button>
            <button className="w-full text-left px-3 py-1.5 hover:bg-slate-50 text-slate-700"
              onClick={() => { setOpen(false); onRoleChange(user.id, 'founder'); }}>Make Founder</button>
            <div className="border-t border-slate-100 my-1" />
            <button className="w-full text-left px-3 py-1.5 hover:bg-red-50 text-red-600 flex items-center gap-2"
              onClick={() => { setOpen(false); onDelete(user.id, user.email); }}>
              <Trash2 className="w-3.5 h-3.5" /> Delete user
            </button>
          </div>
        </>,
        document.body
      )}
    </div>
  );
}

// ─── Users tab ───────────────────────────────────────────────────────────────

function UsersTab({ currentUserId }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try { const res = await adminAPI.getUsers(); setUsers(res.data); }
    catch { toast.error('Failed to load users'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const handleStatusChange = async (userId, status) => {
    try { await adminAPI.updateStatus(userId, status); toast.success(`User ${status === 'blocked' ? 'blocked' : 'unblocked'}`); fetchUsers(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed to update status'); }
  };

  const handleRoleChange = async (userId, role) => {
    try { await adminAPI.updateRole(userId, role); toast.success(`Role updated to ${role}`); fetchUsers(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed to update role'); }
  };

  const handleDelete = async (userId, email) => {
    if (!window.confirm(`Delete user ${email}? This cannot be undone.`)) return;
    try { await adminAPI.deleteUser(userId); toast.success('User deleted'); fetchUsers(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed to delete user'); }
  };

  return (
    <div className="rounded-xl border border-slate-200">
      <div className="flex items-center justify-between px-5 py-3 bg-slate-50 border-b border-slate-200 rounded-t-xl">
        <p className="text-sm font-medium text-slate-600">{users.length} users</p>
        <button onClick={fetchUsers} className="inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-700 transition-colors">
          <RefreshCw className="w-3.5 h-3.5" /> Refresh
        </button>
      </div>
      <div className="overflow-x-auto rounded-b-xl">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-white border-b border-slate-100">
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-400 uppercase tracking-wider">Name</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-400 uppercase tracking-wider">Email</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-400 uppercase tracking-wider">Role</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-400 uppercase tracking-wider">Status</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-400 uppercase tracking-wider">Last Login</th>
              <th className="text-right px-4 py-3 text-xs font-semibold text-slate-400 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-50">
            {loading ? (
              Array.from({ length: 5 }).map((_, i) => <SkeletonRow key={i} cols={6} />)
            ) : users.length === 0 ? (
              <tr><td colSpan={6} className="text-center py-10 text-slate-400 text-sm">No users found</td></tr>
            ) : (
              users.map((u) => (
                <tr key={u.id} className="hover:bg-slate-50 transition-colors">
                  <td className="px-4 py-3 font-medium text-slate-900">{u.name}</td>
                  <td className="px-4 py-3 text-slate-500">{u.email}</td>
                  <td className="px-4 py-3"><RoleBadge role={u.role} /></td>
                  <td className="px-4 py-3"><StatusBadge status={u.status} /></td>
                  <td className="px-4 py-3 text-slate-400">{formatTs(u.last_login)}</td>
                  <td className="px-4 py-3 text-right">
                    <ActionMenu user={u} currentUserId={currentUserId} onStatusChange={handleStatusChange} onRoleChange={handleRoleChange} onDelete={handleDelete} />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Access logs ─────────────────────────────────────────────────────────────

function AccessLogsTable() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    adminAPI.getAccessLogs()
      .then((res) => setLogs(res.data))
      .catch(() => toast.error('Failed to load access logs'))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="rounded-xl border border-slate-200 overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-slate-50 border-b border-slate-200">
            {['Name','Email','Event','IP','City','Region','Country','Device','Time'].map(h => (
              <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-slate-400 uppercase tracking-wider">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-50">
          {loading ? (
            Array.from({ length: 6 }).map((_, i) => <SkeletonRow key={i} cols={9} />)
          ) : logs.length === 0 ? (
            <tr><td colSpan={9} className="text-center py-10 text-slate-400 text-sm">No access logs yet</td></tr>
          ) : (
            logs.map((log) => (
              <tr key={log.id} className="hover:bg-slate-50 transition-colors">
                <td className="px-4 py-3 font-medium text-slate-900">{log.user_name}</td>
                <td className="px-4 py-3 text-slate-500">{log.user_email}</td>
                <td className="px-4 py-3"><span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700 capitalize">{log.event}</span></td>
                <td className="px-4 py-3 text-slate-500 font-mono text-xs">{log.ip_address}</td>
                <td className="px-4 py-3 text-slate-500">{log.city || '—'}</td>
                <td className="px-4 py-3 text-slate-500">{log.region || '—'}</td>
                <td className="px-4 py-3 text-slate-500">{log.country || '—'}</td>
                <td className="px-4 py-3 text-slate-400 text-xs">{truncateUA(log.user_agent)}</td>
                <td className="px-4 py-3 text-slate-400 text-xs whitespace-nowrap">{formatTs(log.timestamp)}</td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

// ─── Audit logs ──────────────────────────────────────────────────────────────

function AuditLogsTable() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    adminAPI.getAuditLogs()
      .then((res) => setLogs(res.data))
      .catch(() => toast.error('Failed to load audit logs'))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="rounded-xl border border-slate-200 overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-slate-50 border-b border-slate-200">
            {['Name','Email','Action','Resource','Details','IP','Time'].map(h => (
              <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-slate-400 uppercase tracking-wider">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-50">
          {loading ? (
            Array.from({ length: 6 }).map((_, i) => <SkeletonRow key={i} cols={7} />)
          ) : logs.length === 0 ? (
            <tr><td colSpan={7} className="text-center py-10 text-slate-400 text-sm">No audit logs yet</td></tr>
          ) : (
            logs.map((log) => (
              <tr key={log.id} className="hover:bg-slate-50 transition-colors">
                <td className="px-4 py-3 font-medium text-slate-900">{log.user_name}</td>
                <td className="px-4 py-3 text-slate-500">{log.user_email}</td>
                <td className="px-4 py-3"><span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-700 font-mono">{log.action}</span></td>
                <td className="px-4 py-3 text-slate-500 font-mono text-xs">{log.resource}</td>
                <td className="px-4 py-3 text-slate-400 text-xs max-w-xs truncate">{log.details || '—'}</td>
                <td className="px-4 py-3 text-slate-500 font-mono text-xs">{log.ip_address}</td>
                <td className="px-4 py-3 text-slate-400 text-xs whitespace-nowrap">{formatTs(log.timestamp)}</td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

function LogsSection() {
  const [activeSubTab, setActiveSubTab] = useState('access');
  return (
    <div>
      <div className="flex gap-1 mb-4 p-1 bg-slate-100 rounded-lg w-fit">
        {[{ key: 'access', label: 'Access Logs' }, { key: 'audit', label: 'Audit Logs' }].map((tab) => (
          <button key={tab.key} onClick={() => setActiveSubTab(tab.key)}
            className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${activeSubTab === tab.key ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>
            {tab.label}
          </button>
        ))}
      </div>
      {activeSubTab === 'access' ? <AccessLogsTable /> : <AuditLogsTable />}
    </div>
  );
}

// ─── User Management tab ─────────────────────────────────────────────────────

function UserManagementTab({ currentUserId }) {
  const [activeSubTab, setActiveSubTab] = useState('users');
  return (
    <div className="space-y-4">
      <div className="flex gap-1 p-1 bg-slate-100 rounded-lg w-fit">
        {[{ key: 'users', label: 'Users', Icon: Users }, { key: 'logs', label: 'Logs', Icon: FileText }].map(({ key, label, Icon }) => (
          <button key={key} onClick={() => setActiveSubTab(key)}
            className={`inline-flex items-center gap-1.5 px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${activeSubTab === key ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>
            <Icon className="w-4 h-4" />{label}
          </button>
        ))}
      </div>
      {activeSubTab === 'users' ? <UsersTab currentUserId={currentUserId} /> : <LogsSection />}
    </div>
  );
}

// ─── General tab ─────────────────────────────────────────────────────────────

function GeneralTab({ user }) {
  const [generatingReport, setGeneratingReport] = useState(false);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: '', role: '' });

  const startEdit = () => { setForm({ name: user?.name || '', role: user?.role || '' }); setEditing(true); };
  const cancelEdit = () => setEditing(false);

  const saveEdit = async () => {
    if (!form.name.trim()) return toast.error('Name cannot be empty');
    setSaving(true);
    try {
      const response = await authAPI.updateMe({ name: form.name.trim(), role: form.role.trim() });
      localStorage.setItem('salty_user', JSON.stringify(response.data));
      window.dispatchEvent(new Event('salty_user_updated'));
      setEditing(false);
      toast.success('Profile updated');
    } catch { toast.error('Failed to update profile'); }
    finally { setSaving(false); }
  };

  const handleGenerateReport = async () => {
    setGeneratingReport(true);
    try {
      const response = await reportsAPI.generate({ report_type: 'executive_summary', period: 'ytd', provider: 'openai' });
      const blob = new Blob([response.data.report_content], { type: 'text/markdown' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `SALTY_Executive_Summary_${new Date().toISOString().split('T')[0]}.md`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url);
      toast.success('Report generated successfully');
    } catch { toast.error('Failed to generate report'); }
    finally { setGeneratingReport(false); }
  };

  const notifications = [
    { label: 'Variance Alerts',      desc: 'Get notified when actuals deviate from AOP', defaultOn: true  },
    { label: 'Cash Runway Warnings', desc: 'Alert when runway drops below 3 months',      defaultOn: true  },
    { label: 'Weekly Digest',        desc: 'Receive weekly performance summary',           defaultOn: false },
  ];

  return (
    <div className="space-y-4">
      {/* Profile */}
      <div className="bg-white border border-slate-200 rounded-xl p-5">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <User className="w-4 h-4 text-primary-600" />
            <h3 className="font-semibold text-slate-900">Profile</h3>
          </div>
          {!editing ? (
            <Button variant="ghost" size="sm" onClick={startEdit} className="gap-1.5 text-slate-500">
              <Pencil className="w-3.5 h-3.5" /> Edit
            </Button>
          ) : (
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="sm" onClick={cancelEdit} className="gap-1.5 text-slate-400"><X className="w-3.5 h-3.5" /> Cancel</Button>
              <Button size="sm" onClick={saveEdit} disabled={saving} className="gap-1.5"><Check className="w-3.5 h-3.5" /> {saving ? 'Saving…' : 'Save'}</Button>
            </div>
          )}
        </div>
        <div className="grid grid-cols-2 gap-x-6 gap-y-3">
          <div>
            <p className="text-xs text-slate-400 mb-1">Name</p>
            {editing
              ? <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} className="h-9 text-sm" />
              : <p className="text-sm font-medium text-slate-800 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">{user?.name}</p>
            }
          </div>
          <div>
            <p className="text-xs text-slate-400 mb-1">Email</p>
            <p className="text-sm font-medium text-slate-800 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">{user?.email}</p>
          </div>
          <div>
            <p className="text-xs text-slate-400 mb-1">Role</p>
            {editing
              ? <Input value={form.role} onChange={e => setForm(f => ({ ...f, role: e.target.value }))} className="h-9 text-sm" />
              : <p className="text-sm font-medium text-slate-800 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 capitalize">{user?.role}</p>
            }
          </div>
          <div>
            <p className="text-xs text-slate-400 mb-1">Member Since</p>
            <p className="text-sm font-medium text-slate-800 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
              {user?.created_at ? new Date(user.created_at).toLocaleDateString('en-IN') : ''}
            </p>
          </div>
        </div>
      </div>

      {/* Notifications */}
      <div className="bg-white border border-slate-200 rounded-xl p-5">
        <div className="flex items-center gap-2 mb-4">
          <Bell className="w-4 h-4 text-primary-600" />
          <h3 className="font-semibold text-slate-900">Notifications</h3>
        </div>
        <div className="divide-y divide-slate-100">
          {notifications.map(({ label, desc, defaultOn }) => (
            <div key={label} className="flex items-center justify-between py-3 first:pt-0 last:pb-0">
              <div>
                <p className="text-sm font-medium text-slate-800">{label}</p>
                <p className="text-xs text-slate-400 mt-0.5">{desc}</p>
              </div>
              <Switch defaultChecked={defaultOn} />
            </div>
          ))}
        </div>
      </div>

      {/* Export & Reports */}
      <div className="bg-white border border-slate-200 rounded-xl p-5">
        <div className="flex items-center gap-2 mb-4">
          <Download className="w-4 h-4 text-primary-600" />
          <h3 className="font-semibold text-slate-900">Export & Reports</h3>
        </div>
        <div className="divide-y divide-slate-100">
          <div className="flex items-center justify-between pb-3">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-primary-50 flex items-center justify-center">
                <FileText className="w-4 h-4 text-primary-600" />
              </div>
              <div>
                <p className="text-sm font-medium text-slate-800">Executive Summary Report</p>
                <p className="text-xs text-slate-400">AI-generated board-ready summary</p>
              </div>
            </div>
            <Button size="sm" onClick={handleGenerateReport} disabled={generatingReport} data-testid="generate-report-button">
              {generatingReport ? 'Generating…' : 'Generate'}
            </Button>
          </div>
          <div className="flex items-center justify-between pt-3">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-slate-100 flex items-center justify-center">
                <FileText className="w-4 h-4 text-slate-400" />
              </div>
              <div>
                <p className="text-sm font-medium text-slate-800">Export to Excel</p>
                <p className="text-xs text-slate-400">Download all data as spreadsheet</p>
              </div>
            </div>
            <span className="text-xs text-slate-400 font-medium border border-slate-200 rounded-md px-2.5 py-1">Coming Soon</span>
          </div>
        </div>
      </div>

      <p className="text-xs text-slate-300 text-right pb-1">Salty Control Tower v1.0.0 · May 2026</p>
    </div>
  );
}

// ─── Clickstream / Activity Logs tab ─────────────────────────────────────────

const EVENT_LABELS = {
  'page.view':               '📄 Page View',
  'aop.cell.edit':           '✏️ Cell Edit',
  'aop.channel.edit':        '✏️ Channel Edit',
  'aop.category.edit':       '✏️ Category Edit',
  'aop.filter.channel':      '🔍 Channel Filter',
  'aop.filter.category':     '🔍 Category Filter',
  'aop.filter.period':       '🔍 Period Filter',
  'aop.filter.fiscal_year':  '📅 FY Change',
  'aop.row.expand':          '▶ Row Expand',
  'aop.row.collapse':        '▼ Row Collapse',
  'aop.rows.expand_all':     '▶▶ Expand All',
  'aop.rows.collapse_all':   '▼▼ Collapse All',
  'aop.edit_mode.on':        '🔓 Edit Mode On',
  'aop.edit_mode.off':       '🔒 Edit Mode Off',
  'aop.plan.save':           '💾 Plan Saved',
  'aop.version.save':        '📌 Version Saved',
  'aop.version.view':        '📋 Versions Viewed',
  'aop.version.restore':     '↩️ Version Restored',
  'aop.import.preview':      '👁 Import Preview',
  'aop.import.confirm':      '📥 Import Confirmed',
  'aop.export':              '📤 Data Exported',
  'aop.template.download':   '📋 Template Downloaded',
  'simulator.run':           '⚡ Simulator Run',
  'report.generate':         '🤖 Report Generated',
  'auth.logout':             '🚪 Logout',
};

function ClickstreamTab() {
  const [events,    setEvents]    = useState([]);
  const [summary,   setSummary]   = useState(null);
  const [loading,   setLoading]   = useState(true);
  const [page,      setPage]      = useState(1);
  const [total,     setTotal]     = useState(0);
  const [filter,    setFilter]    = useState({ event_type: '', user_id: '' });
  const [view,      setView]      = useState('events');  // 'events' | 'summary'
  const LIMIT = 50;

  const fetchEvents = useCallback(async (p = 1) => {
    setLoading(true);
    try {
      const params = { page: p, limit: LIMIT };
      if (filter.event_type) params.event_type = filter.event_type;
      if (filter.user_id)    params.user_id    = filter.user_id;
      const r = await clickstreamAPI.getEvents(params);
      setEvents(r.data.events);
      setTotal(r.data.total);
      setPage(p);
    } catch { toast.error('Failed to load activity logs'); }
    finally { setLoading(false); }
  }, [filter]);

  const fetchSummary = useCallback(async () => {
    setLoading(true);
    try {
      const r = await clickstreamAPI.getSummary();
      setSummary(r.data);
    } catch { toast.error('Failed to load summary'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (view === 'events') fetchEvents(1);
    else fetchSummary();
  }, [view, fetchEvents, fetchSummary]);

  const fmtTs = (ts) => {
    try { return new Date(ts).toLocaleString('en-IN'); } catch { return ts; }
  };

  const totalPages = Math.ceil(total / LIMIT);

  return (
    <div className="space-y-4">
      {/* Header + view toggle */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-semibold text-slate-900">Activity Logs</h3>
          <p className="text-xs text-slate-500 mt-0.5">Every user action tracked in real time · {total.toLocaleString()} total events</p>
        </div>
        <div className="flex gap-1 p-1 bg-slate-100 rounded-lg">
          {[{ k: 'events', l: 'Event Stream' }, { k: 'summary', l: 'Summary' }].map(({ k, l }) => (
            <button key={k} onClick={() => setView(k)}
              className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${view === k ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>
              {l}
            </button>
          ))}
        </div>
      </div>

      {view === 'summary' && summary && (
        <div className="space-y-4">
          {/* Top events */}
          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
            <div className="px-4 py-3 bg-slate-50 border-b border-slate-200 text-xs font-semibold text-slate-500 uppercase tracking-wider">Top Event Types</div>
            <div className="divide-y divide-slate-100">
              {summary.top_events.map(e => (
                <div key={e._id} className="flex items-center justify-between px-4 py-2.5">
                  <span className="text-sm text-slate-700">{EVENT_LABELS[e._id] || e._id}</span>
                  <span className="text-sm font-semibold font-mono text-slate-900">{e.count.toLocaleString()}</span>
                </div>
              ))}
            </div>
          </div>
          {/* Active users */}
          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
            <div className="px-4 py-3 bg-slate-50 border-b border-slate-200 text-xs font-semibold text-slate-500 uppercase tracking-wider">Most Active Users (Last 7 Days)</div>
            <div className="divide-y divide-slate-100">
              {summary.active_users.map(u => (
                <div key={u._id} className="flex items-center justify-between px-4 py-2.5">
                  <div>
                    <p className="text-sm font-medium text-slate-800">{u.user_name}</p>
                    <p className="text-xs text-slate-400">{u.user_email} · Last seen {fmtTs(u.last_seen)}</p>
                  </div>
                  <span className="text-sm font-semibold font-mono text-slate-900">{u.events} events</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {view === 'events' && (
        <>
          {/* Filters */}
          <div className="flex items-center gap-2">
            <Input placeholder="Filter by event type (e.g. aop.)" value={filter.event_type}
              onChange={e => setFilter(f => ({ ...f, event_type: e.target.value }))}
              className="h-8 text-sm w-56" />
            <Button size="sm" onClick={() => fetchEvents(1)}>Search</Button>
            <button onClick={() => { setFilter({ event_type: '', user_id: '' }); fetchEvents(1); }}
              className="text-xs text-slate-400 hover:text-slate-600">Clear</button>
            <span className="ml-auto text-xs text-slate-400">{total.toLocaleString()} events · Page {page}/{totalPages || 1}</span>
          </div>

          {/* Event table */}
          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
            <div className="overflow-x-auto" style={{ maxHeight: '60vh', overflow: 'auto' }}>
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-slate-100 z-10">
                  <tr>
                    {['Time', 'User', 'Event', 'Page', 'Details'].map(h => (
                      <th key={h} className="text-left px-3 py-2.5 text-xs font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {loading ? (
                    Array.from({ length: 8 }).map((_, i) => (
                      <tr key={i}><td colSpan={5} className="px-3 py-2"><div className="h-4 bg-slate-100 rounded animate-pulse" /></td></tr>
                    ))
                  ) : events.length === 0 ? (
                    <tr><td colSpan={5} className="text-center py-10 text-slate-400">No events found</td></tr>
                  ) : events.map(ev => (
                    <tr key={ev.id} className="hover:bg-slate-50 transition-colors">
                      <td className="px-3 py-2 text-xs text-slate-400 whitespace-nowrap">{fmtTs(ev.timestamp)}</td>
                      <td className="px-3 py-2">
                        <p className="text-xs font-medium text-slate-800">{ev.user_name}</p>
                        <p className="text-xs text-slate-400">{ev.user_email}</p>
                      </td>
                      <td className="px-3 py-2">
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 text-xs font-medium whitespace-nowrap">
                          {EVENT_LABELS[ev.event_type] || ev.event_type}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-xs text-slate-500 font-mono">{ev.page}</td>
                      <td className="px-3 py-2 text-xs text-slate-400 max-w-xs">
                        {Object.entries(ev.metadata || {}).slice(0, 3).map(([k, v]) => (
                          <span key={k} className="mr-2">
                            <span className="text-slate-500">{k}:</span> {String(v).slice(0, 30)}
                          </span>
                        ))}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2">
              <Button variant="outline" size="sm" onClick={() => fetchEvents(page - 1)} disabled={page <= 1}>← Prev</Button>
              <span className="text-sm text-slate-500">Page {page} of {totalPages}</span>
              <Button variant="outline" size="sm" onClick={() => fetchEvents(page + 1)} disabled={page >= totalPages}>Next →</Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}


// ─── Page ────────────────────────────────────────────────────────────────────

export default function SettingsPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [activeTab, setActiveTab] = useState('general');

  const tabs = [
    { key: 'general',     label: 'General' },
    ...(isAdmin ? [
      { key: 'users',      label: 'User Management' },
      { key: 'clickstream',label: 'Activity Logs' },
    ] : []),
  ];

  return (
    <div className="space-y-5" data-testid="settings-page">
      {/* Page header */}
      <div className="flex items-center gap-3">
        <div className="p-2 rounded-xl bg-slate-100">
          <Shield className="w-5 h-5 text-slate-600" />
        </div>
        <div>
          <h1 className="text-xl font-bold text-slate-900">Settings</h1>
          <p className="text-sm text-slate-500">Manage your profile and application settings</p>
        </div>
      </div>

      {/* Tabs — only shown when user is admin (more than one tab) */}
      {tabs.length > 1 && (
        <div className="flex gap-1 p-1 bg-slate-100 rounded-lg w-fit">
          {tabs.map(({ key, label }) => (
            <button key={key} onClick={() => setActiveTab(key)}
              className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${activeTab === key ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>
              {label}
            </button>
          ))}
        </div>
      )}

      {activeTab === 'general'     && <GeneralTab user={user} />}
      {activeTab === 'users'       && <UserManagementTab currentUserId={user?.id} />}
      {activeTab === 'clickstream' && <ClickstreamTab />}
    </div>
  );
}
